import { asyncNoop, cloneDeep, noop } from "es-toolkit";
import type { BaseIssue, BaseSchema, BaseSchemaAsync, SafeParseResult } from "valibot";
import { onBeforeMount, ref, type Ref } from "vue";
import { RequestController } from "../api/controller";
import { alerts } from "../common/alert.common";
import { Log } from "../common/logging.common";
import { v } from "../common/validation.common";

type ParseSchema =
    | BaseSchema<unknown, unknown, BaseIssue<unknown>>
    | BaseSchemaAsync<unknown, unknown, BaseIssue<unknown>>;

export type ParseResult = Promise<SafeParseResult<ParseSchema>>;

export type ResetHookFn = () => void;
export type GenerateHookFn = () => Promise<void>;
export type ScrambleHookFn = () => void;
export type ValidateHookFn = () => Promise<boolean>;

export type GenerateFn<T> = () => Promise<T>;
export type ScrambleFn<T> = (data?: any) => T;
export type ValidateFn<T = unknown> = (data: T) => ParseResult;

export interface AttachReset<T> {
    resetVal: T | undefined;
    detachFn: () => void;
}

export interface AttachGenerate<T> {
    generate: GenerateFn<T>;
    detachFn: () => void;
}

export interface AttachScramble<T> {
    scramble: ScrambleFn<T>;
    detachFn: () => void;
}

export interface AttachValidate<T> {
    validate: ValidateFn<T>;
    detachFn: () => void;
}

export type UpdateStruct<T> = {
    [K in keyof T]: (data: NonNullable<T[K]>) => Promise<boolean>;
};

export type RemoveStruct<T> = {
    [K in keyof T]: () => Promise<boolean>;
};

export type ValidateFields<T> = {
    [K in keyof T]: ParseSchema;
};

export type ValidateStrict<T, F> = {
    [K in keyof F]: K extends keyof T ? F[K] : never;
};

export type ValidateSchema<T> = {
    [K in keyof T]: ValidateFn;
};

export type ValidateStruct<T> = {
    [K in keyof T]: ValidateFn<T[K]>;
};

function parseable(schema: ParseSchema) {
    return async (data: unknown) =>
        await v.safeParseAsync(schema, data, {
            abortEarly: true,
            abortPipeEarly: true,
        });
}

export class FormController<T extends Record<string, unknown>> {
    public readonly initialValue: T;
    public readonly isLoading: Ref<boolean> = ref(false);
    public readonly isSubmitting: Ref<boolean> = ref(false);

    private readonly modelRef: Ref<T>;
    private submitFn: () => Promise<void> = asyncNoop;
    private hookId = 0;

    private resetHooks = new Map<number, ResetHookFn>();
    private generateHooks = new Map<number, GenerateHookFn>();
    private scrambleHooks = new Map<number, ScrambleHookFn>();
    private validateHooks = new Map<number, ValidateHookFn>();

    constructor(initialValue: T) {
        this.initialValue = cloneDeep(initialValue);
        this.modelRef = ref(cloneDeep(initialValue)) as Ref<T>;
    }

    public get models() {
        return this.modelRef.value;
    }

    public get submit() {
        return this.submitFn;
    }

    /*
    // TODO(SUSPENSE): Wait until suspense is stable enough
    public async preload<O>(obtain: RequestController<O>, assignFn: (data: O) => T) {
        const data = ref<O>();
        data.value = await obtain.execute(this.isLoading);
        if (data.value !== undefined) this.modelRef.value = assignFn(data.value);
        return data;
    }
    */

    public initialize<O>(
        obtain: RequestController<O>,
        assignFn: (data: O) => T,
        errorFn?: (message: string) => unknown,
    ): Ref<O | undefined> {
        const data = ref<O>();
        this.isLoading.value = true;

        onBeforeMount(async () => {
            data.value = await obtain.onFailure(errorFn ?? noop).execute(this.isLoading);
            if (data.value !== undefined) {
                this.modelRef.value = assignFn(data.value);
            }
        });

        return data;
    }

    public child() {
        return new FormController<T>(this.initialValue);
    }

    public onSubmit(submitFn: () => Promise<void>) {
        this.submitFn = async () => {
            try {
                this.isSubmitting.value = true;
                await submitFn();
            } finally {
                this.isSubmitting.value = false;
            }
        };
    }

    public updates<S, U extends UpdateStruct<Partial<T>>>(
        submitFn: (data: S) => Promise<boolean>,
        struct: (submit: typeof submitFn) => U,
    ) {
        return struct(submitFn);
    }

    public removes<S, R extends RemoveStruct<Partial<T>>>(
        submitFn: (data: S) => Promise<boolean>,
        struct: (submit: typeof submitFn) => R,
    ) {
        return struct(submitFn);
    }

    public validators<F extends ValidateFields<Partial<T>>>(
        validators: ValidateStrict<T, F>,
    ): {
        schema: v.ObjectSchemaAsync<F, undefined>;
        fields: ValidateSchema<Record<keyof F, ParseSchema>>;
    } {
        return {
            schema: v.objectAsync(validators as F),
            fields: Object.keys(validators).reduce((a, v) => {
                return { ...a, [v]: parseable(validators[v]!) };
            }, {}) as ValidateSchema<Record<keyof F, ParseSchema>>,
        };
    }

    public withValidation<F extends v.ObjectSchemaAsync<any, any>>(
        schema: F,
        submitFn: (data: v.InferOutput<F>) => Promise<void>,
    ) {
        return async () => {
            const parsed = await v.safeParseAsync(schema, this.models);

            if (parsed.success === false) {
                Log.e(parsed.issues);
                alerts.form.incomplete();
                return;
            }

            await submitFn(parsed.output);
        };
    }

    public attachReset<R>(resetValue?: R) {
        return (resetHook: ResetHookFn): AttachReset<R> => {
            const id = this.genHookId();
            this.resetHooks.set(id, resetHook);

            return {
                resetVal: resetValue,
                detachFn: () => {
                    this.resetHooks.delete(id);
                },
            };
        };
    }

    public attachGenerate<G>(generate: GenerateFn<G>) {
        return (generateHook: GenerateHookFn): AttachGenerate<G> => {
            const id = this.genHookId();
            this.generateHooks.set(id, generateHook);

            return {
                generate: generate,
                detachFn: () => {
                    this.generateHooks.delete(id);
                },
            };
        };
    }

    public attachScramble<S>(scramble: ScrambleFn<NonNullable<S>>) {
        return (scrambleHook: ScrambleHookFn): AttachScramble<NonNullable<S>> => {
            const id = this.genHookId();
            this.scrambleHooks.set(id, scrambleHook);

            return {
                scramble: scramble,
                detachFn: () => {
                    this.scrambleHooks.delete(id);
                },
            };
        };
    }

    public attachValidate<V>(validate: ValidateFn<V>) {
        return (validateHook: ValidateHookFn): AttachValidate<V> => {
            const id = this.genHookId();
            this.validateHooks.set(id, validateHook);

            return {
                validate: validate,
                detachFn: () => {
                    this.validateHooks.delete(id);
                },
            };
        };
    }

    public get reset() {
        return () => {
            this.resetHooks.forEach((fn) => fn());
        };
    }

    public get generate() {
        return async () => {
            const hooks = [...this.generateHooks.values()];
            await Promise.all(hooks.map(async (fn) => await fn()));
        };
    }

    public get scramble() {
        if (import.meta.env.MODE === "production") {
            return noop;
        }

        return () => {
            this.scrambleHooks.forEach((fn) => fn());
        };
    }

    public get validate() {
        return async () => {
            try {
                const hooks = [...this.validateHooks.values()];
                const data = await Promise.all(hooks.map(async (fn) => await fn()));
                return data.filter((e) => e === true).length === data.length;
            } catch (e) {
                Log.e(e);
                return false;
            }
        };
    }

    public async isValid() {
        const hooks = [...this.validateHooks.values()];
        const data = await Promise.all(hooks.map(async (fn) => await fn()));
        return data.filter((e) => e === true).length === data.length;
    }

    public async isNotValid() {
        return !(await this.isValid());
    }

    private genHookId() {
        return ++this.hookId;
    }
}
